import { ImageTextProps, ImageTextEditorProps, HtmlContent } from './ImageText.types';

/**
 * HTML 콘텐츠 추출 함수
 * 
 * @param content 컨텐츠 (문자열 또는 객체)
 * @param viewMode 현재 보기 모드 (pc, mobile, tablet)
 * @returns 추출된 HTML 문자열
 */
export function extractHtmlContent(content: HtmlContent, viewMode: string = 'pc'): string {
  if (!content) return '';

  if (typeof content === 'string') {
    return content;
  } 
  
  if (typeof content === 'object' && content !== null) {
    // deviceProperty 구조가 있는 경우
    if (content.deviceProperty && content.deviceProperty[viewMode]) {
      return content.deviceProperty[viewMode];
    }
    
    // html 속성이 있는 경우
    if (content.html) {
      return content.html;
    }
  }
  
  return '';
}

/**
 * 원본 props를 ImageText 컴포넌트에 맞게 변환
 * 
 * @param originalProps 원본 props
 * @returns 변환된 ImageText 컴포넌트 props
 */
export function adaptImageTextProps(originalProps: any): ImageTextProps {
  // 기본값 설정
  const originalContent = originalProps?.content || '';
  const originalComponentProps = originalProps?.componentProps || {};
  const viewMode = originalProps?.viewMode || 'pc';
  
  // 스타일 병합
  const style = {
    ...(originalProps?.style || {}),
    ...(originalComponentProps?.style || {})
  };
  
  // className 설정
  const className = originalProps?.className || originalComponentProps?.className || '';

  // 이미지 속성 설정 (다양한 경로에서 속성을 가져옴)
  const imageUrl = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.imageUrl) || 
    originalComponentProps?.imageUrl || 
    'https://via.placeholder.com/400x300';
  
  const imagePosition = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.imagePosition) || 
    originalComponentProps?.imagePosition || 
    'left';
    
  const imageWidth = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.imageWidth) || 
    originalComponentProps?.imageWidth || 
    '50%';
    
  const textWidth = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.textWidth) || 
    originalComponentProps?.textWidth || 
    '50%';
    
  const gap = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.gap) || 
    originalComponentProps?.gap || 
    '0%';
    
  const verticalAlign = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.verticalAlign) || 
    originalComponentProps?.verticalAlign || 
    'middle';
    
  const useSecondImage = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.useSecondImage) !== undefined ?
    originalComponentProps?.deviceProperty?.[viewMode]?.useSecondImage :
    originalComponentProps?.useSecondImage || 
    false;
    
  const secondImageUrl = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.secondImageUrl) || 
    originalComponentProps?.secondImageUrl || 
    'https://via.placeholder.com/400x300';
  
  const backgroundColor = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.backgroundColor) || 
    originalComponentProps?.backgroundColor || 
    'transparent';
    
  const padding = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.padding) || 
    originalComponentProps?.padding || 
    '20px';
    
  const borderRadius = 
    (originalComponentProps?.deviceProperty?.[viewMode]?.borderRadius) || 
    originalComponentProps?.borderRadius || 
    '0px';

  // 변환된 props 반환
  const adaptedProps: ImageTextProps = {
    id: originalProps?.id || '',
    content: originalContent,
    isPreview: originalProps?.isPreview !== undefined ? originalProps.isPreview : true,
    viewMode,
    style,
    className,
    componentProps: {
      ...originalComponentProps,
      imageUrl,
      imagePosition,
      imageWidth,
      textWidth,
      gap,
      verticalAlign,
      useSecondImage,
      secondImageUrl,
      backgroundColor,
      padding,
      borderRadius
    }
  };

  // 원본 props의 모든 속성 중 이미 처리한 속성 제외하고 나머지 복사
  const excludeKeys = ['id', 'content', 'isPreview', 'viewMode', 'style', 'className', 'componentProps'];
  
  Object.keys(originalProps).forEach(key => {
    if (!excludeKeys.includes(key)) {
      adaptedProps[key] = originalProps[key];
    }
  });

  return adaptedProps;
}

/**
 * 에디터용 props 변환 어댑터
 * 
 * @param originalProps 원본 props
 * @returns 변환된 에디터 컴포넌트 props
 */
export function adaptImageTextEditorProps(originalProps: any): ImageTextEditorProps {
  // 기본 props 변환
  const adaptedProps = adaptImageTextProps(originalProps);
  
  // 에디터 관련 추가 props
  return {
    ...adaptedProps,
    isSelected: originalProps.isSelected || false,
    updateComponent: originalProps.updateComponent || (() => {})
  };
} 